/**
 * Return-path and checkout-intent preservation (APP-001 §8).
 *
 * Approved return paths (e.g. `?redirect=/playground`) are preserved through
 * sign-in/up. Checkout intent (`?plan=Developer&interval=monthly`) is preserved but
 * NOT acted on in APP-001 (billing is deferred). Plan/interval values are
 * validated against a frontend-safe allowlist derived from the backend plan
 * catalog. Stripe Price IDs are never accepted from query parameters.
 */
import { BILLING_INTERVALS, SELF_SERVICE_PLANS, type BillingIntervalValue, type SelfServicePlan } from '@/api/dashboard-types';
import { sanitizeReturnPath } from '@/lib/redirect';

export interface ReturnIntent {
  /** Validated same-origin return path, or null. */
  returnPath: string | null;
  /** Validated plan intent (Developer/Growth/Scale/Business), or null. */
  plan: SelfServicePlan | null;
  /** Validated billing interval, or null. */
  interval: BillingIntervalValue | null;
  /** Serialized form safe to put in a URL search param. */
  asSearchParams(): URLSearchParams;
}

export function parseReturnIntent(search: URLSearchParams | string | undefined): ReturnIntent {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search ?? new URLSearchParams();

  const redirect = params.get('redirect');
  const returnPath = redirect ? sanitizeReturnPath(redirect) : null;

  const rawPlan = params.get('plan');
  const plan =
    rawPlan && (SELF_SERVICE_PLANS as readonly string[]).includes(rawPlan)
      ? (rawPlan as SelfServicePlan)
      : null;

  const rawInterval = params.get('interval');
  const interval =
    rawInterval && (BILLING_INTERVALS as readonly string[]).includes(rawInterval)
      ? (rawInterval as BillingIntervalValue)
      : null;

  return {
    returnPath,
    plan,
    interval,
    asSearchParams() {
      const sp = new URLSearchParams();
      if (returnPath && returnPath !== '/') sp.set('redirect', returnPath);
      if (plan) sp.set('plan', plan);
      if (interval) sp.set('interval', interval);
      return sp;
    },
  };
}
